export const CORRELATION_ID_HEADER = 'x-correlation-id';
export const REQUEST_ID_HEADER = 'x-request-id';
export const PCINIT_HEADER = 'pcinit';
export const PCINIT_HEADER_VALUE = 'set';

const WEB_PCINIT_PATH_PREFIXES = ['/api/web/', '/web/'] as const;

const fallbackRandom = () => Math.random().toString(36).slice(2, 10);

const resolveRequestPathname = (input: RequestInfo | URL) => {
    if (typeof input === 'string') {
        try {
            return new URL(input, 'http://pcinit.local').pathname;
        } catch {
            return null;
        }
    }

    if (input instanceof URL) {
        return input.pathname;
    }

    if (typeof Request !== 'undefined' && input instanceof Request) {
        try {
            return new URL(input.url, 'http://pcinit.local').pathname;
        } catch {
            return null;
        }
    }

    return null;
};

export const shouldRequirePcinitHeader = (input: RequestInfo | URL) => {
    const pathname = resolveRequestPathname(input);

    if (!pathname) {
        return false;
    }

    return WEB_PCINIT_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
};

export const generateCorrelationId = (prefix = 'web') => {
    const normalizedPrefix =
        String(prefix || 'web')
            .trim()
            .replace(/[^A-Za-z0-9._:-]/g, '')
            .slice(0, 16) || 'web';
    const uuid = globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${fallbackRandom()}`;

    return `${normalizedPrefix}-${uuid}`;
};

export const attachCorrelationId = ({
    headers,
    correlationId,
    prefix = 'web',
}: {
    headers?: HeadersInit;
    correlationId?: string | null;
    prefix?: string;
}) => {
    const nextHeaders = new Headers(headers ?? {});
    const existingCorrelationId = nextHeaders.get(CORRELATION_ID_HEADER)?.trim() || nextHeaders.get(REQUEST_ID_HEADER)?.trim() || null;
    const resolvedCorrelationId = correlationId?.trim() || existingCorrelationId || generateCorrelationId(prefix);

    nextHeaders.set(CORRELATION_ID_HEADER, resolvedCorrelationId);

    return {
        headers: nextHeaders,
        correlationId: resolvedCorrelationId,
    };
};

export const getResponseCorrelationId = (response: Response) => {
    const headers = response.headers;

    if (!headers || typeof headers.get !== 'function') {
        return null;
    }

    return headers.get(CORRELATION_ID_HEADER) ?? headers.get(REQUEST_ID_HEADER);
};

export const fetchWithCorrelationId = async (
    input: RequestInfo | URL,
    init?: RequestInit & { correlationId?: string | null; skipGlobalErrorToast?: boolean },
    options?: {
        prefix?: string;
    }
) => {
    const { correlationId, headers } = attachCorrelationId({
        headers: init?.headers,
        correlationId: init?.correlationId ?? null,
        prefix: options?.prefix,
    });
    const normalizedHeaders = new Headers(headers);

    if (shouldRequirePcinitHeader(input)) {
        normalizedHeaders.set(PCINIT_HEADER, PCINIT_HEADER_VALUE);
    }

    const { correlationId: _correlationId, skipGlobalErrorToast, ...rest } = init ?? {};
    void _correlationId;

    const response = await fetch(input, {
        ...rest,
        headers: normalizedHeaders,
    });

    if (!response.ok && !skipGlobalErrorToast) {
        const { extractApiErrorMessage, extractPopToastSeverity } = await import('@/lib/api/errorPayload');
        const { pushAppToast } = await import('@/lib/ui/globalAppToast');
        const payload =
            typeof response.clone === 'function'
                ? await response
                      .clone()
                      .json()
                      .catch(() => null)
                : null;

        pushAppToast({
            message: extractApiErrorMessage(payload),
            severity: extractPopToastSeverity(payload),
        });
    }

    return {
        response,
        correlationId,
    };
};
